import { useSelector } from "react-redux";
import ItemsList from "common/Lists/ItemsList";
import {
  selectClientToExchangeMapping,
  selectPreprocessedPortfolioData,
} from "@shared/slices/portfolio.slice";
import { ListItem } from "common/Lists/types";
import { useEffect } from "react";
import { getItem } from "common/Lists/utils";
import {
  AccountName,
  GetClientsDataFromSymbolRequest,
  LanguageNameSpaces,
  SymbolClientsData,
} from "@shared/types";
import { ICON_TYPES } from "@shared/common/constants";
import { SkipToken, skipToken } from "@reduxjs/toolkit/query";
import { sharedLib } from "@shared/services/shared.service";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { useClientsData } from "@shared/utils/hooks/misc";

interface ClientsListProps {
  coinOrSymbol?: string;
  handleSelectAccount: (
    account?: SymbolClientsData,
    checkConfirm?: boolean
  ) => void;
  isEntry: boolean;
  value?: AccountName;
  getExternalCanTradeErrorMessage?: (
    client: SymbolClientsData
  ) => string | undefined;
  isLoading?: boolean;
}

// TODO: Remove logic relating to portfolioData from this component
const ClientsList = ({
  coinOrSymbol,
  handleSelectAccount,
  isEntry,
  value,
  getExternalCanTradeErrorMessage,
  isLoading,
}: ClientsListProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const args: GetClientsDataFromSymbolRequest | SkipToken = coinOrSymbol
    ? { symbol: coinOrSymbol }
    : skipToken;
  const {
    data: clientsData,
    isFetching: fetching,
    isSuccess: loaded,
  } = sharedLib.sharedApi.trades.useGetClientsDataFromSymbolQuery(args);
  const portfolioData = useSelector(
    isEntry ? () => null : selectPreprocessedPortfolioData
  );
  const clientToExchangeMapping = useSelector(selectClientToExchangeMapping);
  const { getAccountIsReadOnly } = useClientsData();

  const getCanTradeErrorMessage = (client: SymbolClientsData) => {
    if (!client.can_trade) {
      return t("errors.account_not_connected", { symbol: coinOrSymbol });
    }

    if (getExternalCanTradeErrorMessage) {
      return getExternalCanTradeErrorMessage(client);
    }
  };

  const writableActiveClientsData = clientsData?.filter(
    (client) => !getAccountIsReadOnly(client.id)
  );

  let items: ListItem<SymbolClientsData>[];
  if (isEntry) {
    items =
      writableActiveClientsData?.map((client: SymbolClientsData) =>
        getItem(
          client.name,
          client,
          client.exchange,
          getCanTradeErrorMessage(client)
        )
      ) || [];
  } else {
    items =
      portfolioData
        ?.filter(
          (coinData) => coinData.symbol === coinOrSymbol && coinData.is_sellable
        )
        .map((coinData) => {
          const { client: clientName, client_id } = coinData;
          const exchange = clientToExchangeMapping[clientName];
          const client = {
            exchange,
            id: client_id,
            name: "",
            can_trade: false,
          };
          return getItem(clientName, client, exchange);
        }) || [];
  }

  useEffect(() => {
    if (coinOrSymbol && value && !fetching) {
      const canTradeAccountsItems = items.filter((item) => !item.errorMessage);
      if (!canTradeAccountsItems.find((item) => item.displayName === value)) {
        if (canTradeAccountsItems.length) {
          handleSelectAccount(
            canTradeAccountsItems[0].selectHandlerItem,
            false
          );
        }
      }
    }
  }, [coinOrSymbol, items, fetching]);

  const props = {
    items,
    loading: isLoading || fetching,
    loaded,
    handleSelectItem: handleSelectAccount,
    iconType: ICON_TYPES.EXCHANGE,
  };

  return <ItemsList {...props} />;
};

export default ClientsList;
